"use client";

import { useMemo, useState } from "react";
import { SEGMENT_OFF, SEGMENT_ON, SELECTED, UNSELECTED } from "@/lib/ui-classes";
import Link from "@/lib/nav-link";
import { ArrowRight, Search } from "lucide-react";
import ListingsLocationPicker from "@/components/listings/ListingsLocationPicker";
import Photo from "@/components/Photo";

/**
 * The /menziller and /evler start page, interactive part.
 *
 * One switch (buy / rent) drives the whole page: the search, the
 * district photo cards and their numbers, the popular searches and the
 * area chips. Both deals are rendered and the inactive one is hidden,
 * so every link is in the server HTML for crawlers while a visitor only
 * ever sees one set.
 *
 * The area field is the /listings location picker itself (owner,
 * 2026-09-24: no native select). One area with a page of its own lands
 * on that search page, in the canonical segment order so there is no
 * redirect hop; several areas, or one without a page, open /listings
 * with the same areas and rooms applied.
 */

type Deal = "satis" | "kiraye";
type Side = { count: number; median_ppm: number | null; path: string | null };

export interface HubData {
  baku: { sale: Side; rent: Side };
  districts: Array<{ slug: string; id: string; name: string; sale: Side; rent: Side; photo: string | null }>;
  intents: Array<{ path: string; deal: Deal; count: number; label: Record<string, string>; median_ppm: number | null; photo: string | null }>;
  places: Array<{ slug: string; id: string; name: string; type: "metro" | "qesebe" | "nisangah"; sale: Side; rent: Side }>;
}

export interface HubLabels {
  buy: string; rent: string; area: string; allBaku: string; districts: string; metros: string; qesebe: string;
  rooms: string; search: string; listings: string; perM2: string; perM2Month: string;
  districtsTitle: string; popularTitle: string; placesTitle: string; landmarksTitle: string; showAll: string;
}

const card = (u: string) => u.replace(/\/uploads\/(thumbnail|full)\//, "/uploads/f460x345/");
const group = (n: number) => Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
const dec = (n: number, locale: string) =>
  n < 100 && !Number.isInteger(n) ? n.toFixed(1).replace(".", locale === "en" ? "." : ",") : group(n);

const PLACES_FOLDED = 18;

export default function HubClient({ locale, family = "menziller", data, labels }: {
  locale: string; family?: "menziller" | "evler"; data: HubData; labels: HubLabels;
}) {
  const [deal, setDeal] = useState<Deal>("satis");
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [rooms, setRooms] = useState<string | null>(null);
  const rent = deal === "kiraye";
  const sideOf = (x: { sale: Side; rent: Side }, d: Deal) => (d === "kiraye" ? x.rent : x.sale);
  const ppm = (v: number | null, d: Deal) =>
    v == null ? null : `${dec(v, locale)} ${d === "kiraye" ? labels.perM2Month : labels.perM2}`;

  // Taxonomy id -> the area's own search page, where it has one.
  const slugById = useMemo(() => {
    const m = new Map<string, string>();
    for (const x of [...data.districts, ...data.places]) if (x.id) m.set(String(x.id), x.slug);
    return m;
  }, [data]);

  const href = useMemo(() => {
    const one = areaIds.length === 1 ? slugById.get(areaIds[0]) : undefined;
    if (areaIds.length === 0 || one) {
      const segs = [one ?? "", deal, rooms ? `${rooms}-otaqli` : ""].filter(Boolean);
      return `/${locale}/${family}/${segs.join("/")}`;
    }
    // /evler covers every category, so it sends none.
    const q = new URLSearchParams({ leased: rent ? "true" : "false" });
    if (family === "menziller") q.set("category", "apartment");
    areaIds.forEach((id) => q.append("location_ids", id));
    if (rooms) q.append("rooms", rooms);
    return `/${locale}/listings?${q.toString()}`;
  }, [locale, family, areaIds, slugById, deal, rent, rooms]);

  const baku = sideOf(data.baku, deal);
  const seg = (on: boolean) =>
    `flex-1 rounded-lg px-4 py-2 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      on ? SEGMENT_ON : SEGMENT_OFF
    }`;
  const chip = (on: boolean) =>
    `h-9 min-w-9 rounded-lg border px-3 text-sm font-semibold tabular-nums transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
      on ? SELECTED : UNSELECTED
    }`;

  return (
    <div className="space-y-8">
      {/* Search */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
        <div className="flex max-w-xs rounded-xl bg-gray-100 p-1" role="group" aria-label={`${labels.buy} / ${labels.rent}`}>
          <button type="button" aria-pressed={!rent} onClick={() => setDeal("satis")} className={seg(!rent)}>{labels.buy}</button>
          <button type="button" aria-pressed={rent} onClick={() => setDeal("kiraye")} className={seg(rent)}>{labels.rent}</button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto_auto] md:items-end">
          <div className="min-w-0">
            <span className="mb-1 block text-xs font-semibold text-gray-500">{labels.area}</span>
            <ListingsLocationPicker
              selectedIds={areaIds}
              onChange={setAreaIds}
              onApply={(ids) => setAreaIds(ids)}
            />
          </div>
          <div>
            <span className="mb-1 block text-xs font-semibold text-gray-500">{labels.rooms}</span>
            <div className="flex gap-1.5" role="group" aria-label={labels.rooms}>
              {["1", "2", "3", "4", "5"].map((r) => (
                <button key={r} type="button" aria-pressed={rooms === r} onClick={() => setRooms(rooms === r ? null : r)} className={chip(rooms === r)}>
                  {r === "5" ? "5+" : r}
                </button>
              ))}
            </div>
          </div>
          <Link
            href={href}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-indigo-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
          >
            <Search className="h-4 w-4" aria-hidden />
            {labels.search}
          </Link>
        </div>
      </section>

      {/* Districts */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold tracking-tight text-gray-900">{labels.districtsTitle}</h2>
        {(["satis", "kiraye"] as const).map((d) => (
          <ul key={d} className={`grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 ${d === deal ? "" : "hidden"}`}>
            {data.districts.filter((x) => sideOf(x, d).path).map((x, i) => {
              const s = sideOf(x, d);
              return (
                <li key={x.slug}>
                  <PhotoCard
                    href={`/${locale}${s.path}`}
                    photo={x.photo}
                    title={x.name}
                    line={`${group(s.count)} ${labels.listings}`}
                    figure={ppm(s.median_ppm, d)}
                    // Two rows on a phone, the first row on a desktop: eight
                    // high-priority photos made the first ones wait (2026-10-19).
                    priority={d === "satis" && i < 4}
                  />
                </li>
              );
            })}
          </ul>
        ))}
      </section>

      {/* Popular searches */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold tracking-tight text-gray-900">{labels.popularTitle}</h2>
        {(["satis", "kiraye"] as const).map((d) => (
          <ul key={d} className={`grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 ${d === deal ? "" : "hidden"}`}>
            {data.intents.filter((x) => x.deal === d).map((x) => (
              <li key={x.path}>
                <PhotoCard
                  href={`/${locale}${x.path}`}
                  photo={x.photo}
                  title={x.label[locale] ?? x.label.az}
                  line={`${group(x.count)} ${labels.listings}`}
                  figure={ppm(x.median_ppm, d)}
                />
              </li>
            ))}
          </ul>
        ))}
      </section>

      {/* Every other area, then the landmarks, as chips */}
      {([
        { key: "areas", title: labels.placesTitle, items: data.places.filter((p) => p.type !== "nisangah") },
        { key: "landmarks", title: labels.landmarksTitle, items: data.places.filter((p) => p.type === "nisangah") },
      ] as const).map((g) => g.items.length > 0 && (
        <PlaceChips key={g.key} title={g.title} items={g.items} deal={deal} locale={locale} showAll={labels.showAll} sideOf={sideOf} />
      ))}
    </div>
  );
}

function PhotoCard({
  href, photo, title, line, figure, priority = false,
}: {
  href: string; photo: string | null; title: string; line: string; figure: string | null; priority?: boolean;
}) {
  return (
    <Link
      href={href}
      className="group relative block aspect-[4/3] overflow-hidden rounded-2xl bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
    >
      {photo && (
        <Photo
          src={card(photo)}
          alt=""
          className="transition-transform duration-500 ease-out group-hover:scale-[1.02]"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
        />
      )}
      <span className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/35 to-transparent" aria-hidden />
      <span className="absolute inset-x-0 bottom-0 p-3">
        <span className="block text-[15px] font-bold leading-tight text-white">{title}</span>
        <span className="mt-0.5 block text-xs tabular-nums text-white/85">
          {line}{figure ? ` · ${figure}` : ""}
        </span>
      </span>
    </Link>
  );
}

type Place = HubData["places"][number];

/** One folded chip list of areas with a page, per deal (the inactive
 *  deal is rendered hidden, so crawlers see every link). */
function PlaceChips({ title, items, deal, locale, showAll, sideOf }: {
  title: string; items: readonly Place[]; deal: Deal; locale: string; showAll: string;
  sideOf: (x: Place, d: Deal) => Side;
}) {
  const [allPlaces, setAllPlaces] = useState(false);
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-bold tracking-tight text-gray-900">{title}</h2>
      {(["satis", "kiraye"] as const).map((d) => {
        const shown = items.filter((p) => sideOf(p, d).path).sort((a, b) => sideOf(b, d).count - sideOf(a, d).count);
        return (
          <ul key={d} className={`flex flex-wrap gap-1.5 ${d === deal ? "" : "hidden"}`}>
            {shown.map((p, i) => (
              <li key={p.slug} className={!allPlaces && i >= PLACES_FOLDED ? "hidden" : ""}>
                <Link
                  href={`/${locale}${sideOf(p, d).path}`}
                  className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-800 transition-colors hover:border-gray-300 hover:text-gray-900"
                >
                  {p.name}
                  <span className="text-xs tabular-nums text-gray-400">{group(sideOf(p, d).count)}</span>
                </Link>
              </li>
            ))}
            {!allPlaces && shown.length > PLACES_FOLDED && (
              <li>
                <button
                  type="button"
                  onClick={() => setAllPlaces(true)}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  {showAll.replace("{n}", String(shown.length - PLACES_FOLDED))}
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </button>
              </li>
            )}
          </ul>
        );
      })}
    </section>
  );
}
